import { apiConfig, http } from "@api/base";
import { getSearchTotal, prepareDateQuery, prepareSumQuery } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializer";

const apiPaths = {
  list: "/calendar/events/",
  item: "/calendar/events/:eventId",
  create: "/calendar/events/",
  update: "/calendar/events/:eventId",
  delete: "/calendar/events/:eventId",
};

const get = async (eventId) => {
  const path = generatePath(apiPaths.item, { eventId: eventId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const create = async (eventData) => {
  const currentUser = sessionManager.user;

  const backendPayload = serializer.toJSON(eventData);
  backendPayload.created_by = currentUser?.username || currentUser?.email;

  const response = await http.post(apiPaths.create, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const update = async (eventId, eventData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.update, { eventId: eventId });

  const backendPayload = serializer.toJSON(eventData);
  backendPayload.updated_by = currentUser?.username || currentUser?.email;

  const response = await http.put(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

const deleteEvent = async (eventId) => {
  const path = generatePath(apiPaths.delete, { eventId: eventId });
  const response = await http.delete(path);
  return response;
};

class QueryBuilder {
  constructor() {
    this.titleQuery = [];
    this.dateRangeQuery = [];
    this.colorQuery = [];
    this.createdByQuery = [];
    this.academicYearQuery = [];
    this.eventTypeQuery = [];
    this.activeQuery = [];
    this.size = "";
    this.page = "";
    this.sortBy = "";
  }

  withTitle(title) {
    if (!title || _isEmpty(title)) {
      throw TypeError("Title argument missing");
    }
    this.titleQuery.push(`title:"${prepareSumQuery(title)}"`);
    return this;
  }

  withDateRange({ fromDate, toDate }) {
    if (fromDate || toDate) {
      this.dateRangeQuery.push(
        prepareDateQuery("start_date", null, fromDate, toDate)
      );
    }
    return this;
  }

  withColor(color) {
    if (!color || _isEmpty(color)) {
      throw TypeError("Color argument missing");
    }
    this.colorQuery.push(`color:"${prepareSumQuery(color)}"`);
    return this;
  }

  withCreatedBy(username) {
    if (!username || _isEmpty(username)) {
      throw TypeError("Created by argument missing");
    }
    this.createdByQuery.push(`created_by:"${prepareSumQuery(username)}"`);
    return this;
  }

  withAcademicYear(academicYear) {
    if (!academicYear || _isEmpty(academicYear)) {
      throw TypeError("Academic year argument missing");
    }
    this.academicYearQuery.push(
      `academic_year:"${prepareSumQuery(academicYear)}"`
    );
    return this;
  }

  withEventType(eventType) {
    if (!eventType || _isEmpty(eventType)) {
      throw TypeError("Event type argument missing");
    }
    this.eventTypeQuery.push(`event_type:"${prepareSumQuery(eventType)}"`);
    return this;
  }

  activeOnly() {
    this.activeQuery.push("is_active:true");
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `&size=${size}`;
    return this;
  }

  withPage(page = 0) {
    if (page > 0) this.page = `&page=${page}`;
    return this;
  }

  sortByNewest() {
    this.sortBy = `&sort=-created_at`;
    return this;
  }

  sortByStartDate() {
    this.sortBy = `&sort=start_date`;
    return this;
  }

  sortByStartDateDesc() {
    this.sortBy = `&sort=-start_date`;
    return this;
  }

  sortByTitle() {
    this.sortBy = `&sort=title`;
    return this;
  }

  qs() {
    const searchCriteria = this.titleQuery
      .concat(
        this.dateRangeQuery,
        this.colorQuery,
        this.createdByQuery,
        this.academicYearQuery,
        this.eventTypeQuery,
        this.activeQuery
      )
      .join(" AND ");
    return `${searchCriteria}${this.sortBy}${this.size}${this.page}`;
  }
}

const queryBuilder = () => {
  return new QueryBuilder();
};

const list = async (query = "") => {
  const queryString = query ? `?q=${query}` : "";
  const response = await http.get(`${apiPaths.list}${queryString}`);

  if (response.data.status === "success" && response.data.data) {
    const events = Array.isArray(response.data.data) ? response.data.data : [];
    response.data.hits = events.map((item) => serializer.fromJSON(item));
    response.data.total = response.data.total || events.length;
  } else if (response.data.hits) {
    response.data.total = getSearchTotal(response.data.hits);
    response.data.hits = response.data.hits.hits.map((hit) =>
      serializer.fromJSON(hit)
    );
  } else if (response.data.data) {
    response.data.hits = response.data.data.map((item) =>
      serializer.fromJSON(item)
    );
    response.data.total = response.data.total || response.data.data.length;
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

const count = async (query) => {
  const response = await http.get(`${apiPaths.list}?q=${query}`);
  response.data = getSearchTotal(response.data.hits);
  return response;
};

const getCurrentAcademicYearEvents = async () => {
  const response = await list("");

  if (response.data.hits) {
    response.data.hits = response.data.hits.filter(
      (event) =>
        event.is_active !== false &&
        (event.academic_year === "2025-2026" ||
          event.academic_year === "2025-20")
    );
    response.data.total = response.data.hits.length;
  }

  return response;
};

const getEventsByDateRange = async (startDate, endDate) => {
  const query = queryBuilder()
    .withDateRange({ fromDate: startDate, toDate: endDate })
    .activeOnly()
    .sortByStartDate()
    .qs();

  return await list(query);
};

const getEventsByType = async (eventType) => {
  const query = queryBuilder()
    .withEventType(eventType)
    .activeOnly()
    .sortByStartDate()
    .qs();

  return await list(query);
};

export const calendarApi = {
  searchBaseURL: `${apiConfig.baseURL}${apiPaths.list}`,
  query: queryBuilder,
  list: list,
  get: get,
  create: create,
  update: update,
  delete: deleteEvent,
  count: count,
  getCurrentAcademicYearEvents: getCurrentAcademicYearEvents,
  getEventsByDateRange: getEventsByDateRange,
  getEventsByType: getEventsByType,
  serializer: serializer,
};
